import { NavLink } from "react-router-dom";
import { Mark, OpsIcon, RadarIcon, SearchIcon, TableIcon, ThemeIcon } from "./icons.js";
import { useTheme } from "../lib/theme.js";

/**
 * The routes, and the two things beside them that are not routes.
 *
 * A route that is not implemented is absent rather than present-and-dead.
 *
 * ## One component, two shapes: the desk's rail and the phone's bottom bar
 *
 * On the desk this is the icon rail down the left. Under the stylesheet's shell
 * breakpoint it becomes a bar across the bottom, each route a full thumb target within
 * reach of the hand. `design/PhoneRadar.dc.html` draws both.
 *
 * **The Mark and the theme control do not go in the bar.** A setting among the tabs reads
 * as another destination. They go to the header strip instead, which is why they are
 * wrapped separately here. Both wrappers dissolve on the desk (`display: contents`), so the
 * rail stays one flex column; on the phone they are the two pieces of chrome at opposite
 * edges of the screen.
 *
 * ## Collapsed, in the reader: a desk state, and only a desk state
 *
 * The reader is a column of text, and on the desk the rail takes width from it. Collapsed,
 * it keeps the mark, and the mark brings the rest back **as an overlay, not as a column**:
 * re-widening the grid would reflow the text and move every line break mid-read.
 *
 * A bottom bar takes no width, so the phone does not collapse; `RadarLayout` passes
 * `collapsed` accordingly. The mark is a button only while it has an overlay to open.
 */
export function Rail({
  unguarded = false,
  collapsed = false,
  open = false,
  onToggle,
}: {
  unguarded?: boolean;
  /** True on the reader, on the desk, where the icons are hidden until the mark is pressed. */
  collapsed?: boolean;
  open?: boolean;
  onToggle?: () => void;
}) {
  const { resolved, toggle } = useTheme();
  const title = unguarded ? "Running unguarded — no OAuth configured" : "Mosaic";

  return (
    <nav
      className={`rail${collapsed ? " collapsed" : ""}${collapsed && open ? " open" : ""}`}
      aria-label="Sections"
    >
      <div className="railchrome">
        {collapsed ? (
          <button
            type="button"
            className="mark"
            title={title}
            aria-label={open ? "Hide navigation" : "Show navigation"}
            aria-expanded={open}
            onClick={onToggle}
          >
            <Mark />
          </button>
        ) : (
          <div className="mark" title={title}>
            <Mark />
          </div>
        )}
        {/*
          Last in the column on the desk and last in the header on the phone, but second
          in the DOM, because it shares a wrapper with the mark. `order` in the stylesheet
          puts it back at the foot of the rail, so there is one control, not a second
          copy hidden at one width.
        */}
        {/*
          Named for what pressing it does, not for a setting: "system" names a source, and
          on a dark-preferring machine it looks identical to "dark".
        */}
        <button
          className="themebtn"
          onClick={toggle}
          title={`Switch to ${resolved === "dark" ? "light" : "dark"} mode`}
          aria-label={`${resolved === "dark" ? "Dark" : "Light"} mode. Switch to ${
            resolved === "dark" ? "light" : "dark"
          }.`}
        >
          <ThemeIcon />
        </button>
      </div>

      {/*
        The same routes at every width. A label rides with each icon: hidden on the desk,
        where the rail is icons with tooltips, and shown in the bar, where the target has
        room for the word.
      */}
      <div className="railtabs">
        <NavLink to="/" end className={({ isActive }) => (isActive ? "on" : "")} title="Radar">
          <RadarIcon />
          <em>Radar</em>
        </NavLink>
        <NavLink to="/table" className={({ isActive }) => (isActive ? "on" : "")} title="Table mode">
          <TableIcon />
          <em>Table</em>
        </NavLink>
        {/*
          The library, a peer of the radar rather than a control inside it: the radar is
          the situations Mosaic has assembled, and this searches every filing ingested,
          including those that opened no situation.
        */}
        <NavLink to="/search" className={({ isActive }) => (isActive ? "on" : "")} title="Search the library">
          <SearchIcon />
          <em>Search</em>
        </NavLink>
        <NavLink to="/ops" className={({ isActive }) => (isActive ? "on" : "")} title="Operations">
          <OpsIcon />
          <em>Ops</em>
        </NavLink>
      </div>
    </nav>
  );
}
